import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { RsvpForm } from '@/src/modules/rsvp';

const META: TemplateMetadata = {
  id: 'seed-anniversary-golden',
  name: 'Anniversary Golden',
  occasionType: 'ANNIVERSARY',
  description: 'Warm burgundy and gold romantic layout for milestones.',
  isPremium: false,
  tags: ['romantic', 'elegant', 'gold', 'milestone'],
  colorScheme: { primary: '#9f1239', accent: '#b45309', bg: '#fdf6ec' },
};

function fmt(iso: string, style: 'date' | 'time'): string {
  const d = new Date(iso);
  if (style === 'date') {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
  }
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(d);
}

export function AnniversaryGolden({ data }: { data: InvitationData }) {
  const [hero, ...gallery] = data.mediaUrls ?? [];
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <div
      className="min-h-screen font-serif flex items-center justify-center p-8"
      style={{ background: 'linear-gradient(135deg, #fdf6ec 0%, #fff5f5 100%)' }}
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-amber-100">
        {/* gold top bar */}
        <div className="bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600 h-2" />

        {/* header */}
        <div className="bg-rose-900 px-10 py-12 text-center text-white">
          <p className="text-amber-300 text-xs uppercase tracking-[0.3em] font-sans mb-4">
            Celebrating Love
          </p>
          <div className="text-4xl mb-4">♥</div>
          <h1 className="text-4xl font-bold leading-tight mb-2">{data.eventName}</h1>
          <p className="text-rose-200 text-sm font-sans">
            {data.hostName}
          </p>
        </div>

        {/* hero */}
        {hero && (
          <div className="w-full aspect-video overflow-hidden border-y-4 border-amber-100">
            <img src={hero} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="px-10 py-12 text-center">
          {/* gold divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-amber-200" />
            <span className="text-amber-500 text-2xl">✦</span>
            <div className="flex-1 h-px bg-amber-200" />
          </div>

          {/* date & venue */}
          <div className="grid grid-cols-2 gap-6 mb-10">
            <div className="text-left border border-amber-100 rounded-xl p-5 bg-amber-50">
              <p className="text-[10px] text-amber-600 uppercase tracking-widest font-sans mb-2">
                Date &amp; Time
              </p>
              <p className="text-stone-800 font-semibold text-sm leading-snug">
                {fmt(data.eventDate, 'date')}
              </p>
              <p className="text-stone-500 text-xs font-sans mt-1">
                {fmt(data.eventDate, 'time')}
              </p>
            </div>
            <div className="text-left border border-amber-100 rounded-xl p-5 bg-amber-50">
              <p className="text-[10px] text-amber-600 uppercase tracking-widest font-sans mb-2">
                Venue
              </p>
              <p className="text-stone-800 font-semibold text-sm leading-snug">
                {data.eventVenue}
              </p>
            </div>
          </div>

          {/* message */}
          {data.customMessage && (
            <div className="relative mb-10 px-8">
              <span className="absolute -top-3 left-6 text-5xl text-amber-200 font-serif leading-none">"</span>
              <p className="text-stone-600 italic text-lg leading-relaxed relative z-10">
                {data.customMessage}
              </p>
              <span className="absolute -bottom-4 right-6 text-5xl text-amber-200 font-serif leading-none">"</span>
            </div>
          )}

          {/* gallery */}
          {gallery.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-6 mb-10">
              {gallery.slice(0, 3).map((url, i) => (
                <div key={i} className="aspect-square rounded-lg overflow-hidden border-2 border-amber-100">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* RSVP */}
          <div style={{ background: 'rgba(159,18,57,0.85)', borderRadius: 20, padding: '24px 20px' }}>
            <RsvpForm
              invitationId={invitationId}
              isLive={isLive}
              externalLink={data.rsvpLink || null}
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600 h-2" />
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: AnniversaryGolden });
